import { useCallback, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api, ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { useCountry } from '@/lib/country'
import { useAsync } from '@/lib/useAsync'
import { useUnsavedChanges } from '@/lib/useUnsavedChanges'
import { AuditLogPanel } from '@/components/AuditLog'
import { LeaveGuard, useLeaveGuard } from '@/components/LeaveGuard'
import { PageHeader, Tabs } from '@/components/ui/page'
import { DEFAULT_PAGE_SIZE, FilterSelect, ListToolbar, Pagination } from '@/components/ui/listing'
import { Badge, Busy, Button, Card, CardHeader, EmptyState, ErrorState, Loading } from '@/components/ui/primitives'
import { Dialog } from '@/components/ui/dialog'
import { Field, FormGrid, Input, SaveActions, Select, Textarea } from '@/components/ui/form'
import { SortableTh, Table, Td, Th, Tr } from '@/components/ui/table'
import { useToast } from '@/components/ui/toast'
import type {
  AssignableUser,
  Paged,
  SalesTeamDetail,
  SalesTeamListItem,
  UserListItem,
} from '@/types/api'

/**
 * Where a header's third click lands, and what the toolbar shows for it.
 * A real sort string rather than an empty one, so the two controls always agree.
 */
const DEFAULT_SORT = 'name'

/** What the form edits, and therefore what an unsaved change can be about. */
const EDITED = ['name', 'description', 'managerUserId', 'isActive'] as const

export function Teams() {
  const { can } = useAuth()
  const { available } = useCountry()
  const navigate = useNavigate()
  const toast = useToast()
  const [creating, setCreating] = useState(false)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [countryId, setCountryId] = useState('')
  const [sort, setSort] = useState(DEFAULT_SORT)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  const manage = can('salesteam.manage')

  /** Any change to what is being shown puts the reader back on page one. */
  function refine(change: () => void) {
    change()
    setPage(1)
  }

  const list = useAsync<Paged<SalesTeamListItem>>(
    (signal) =>
      api.get<Paged<SalesTeamListItem>>(
        '/sales-teams',
        {
          includeInactive: status !== 'active',
          search,
          countryId: countryId || undefined,
          sort,
          page,
          pageSize,
        },
        signal,
      ),
    [status, search, countryId, sort, page, pageSize],
  )

  return (
    <>
      <PageHeader
        eyebrow="Administration"
        title="Sales Teams"
        hint="A reporting line, not a territory. A rep sees their own work; a manager sees everyone reporting into them, in the countries they themselves hold."
        actions={
          manage && (
            <Button variant="primary" onClick={() => setCreating(true)}>
              New team
            </Button>
          )
        }
      />

      <ListToolbar
        sort={sort}
        onSortChange={(next) => refine(() => setSort(next))}
        sortChoices={[
          { value: 'name', label: 'Team A–Z' },
          { value: '-name', label: 'Team Z–A' },
          { value: 'manager', label: 'Manager A–Z' },
          { value: '-members', label: 'Largest first' },
          { value: 'status', label: 'Archived first' },
        ]}
      >
        <div className="w-64">
          <Input
            placeholder="Search team or manager"
            value={search}
            onChange={(event) => refine(() => setSearch(event.target.value))}
          />
        </div>

        <FilterSelect label="Status" value={status} onChange={(next) => refine(() => setStatus(next))}>
          <option value="">Active and archived</option>
          <option value="active">Active only</option>
        </FilterSelect>

        {/* A team is not scoped to a country, so this asks the only question with an
            answer: does anybody on it work there. */}
        <FilterSelect label="Covers" value={countryId} onChange={(next) => refine(() => setCountryId(next))}>
          <option value="">Anywhere</option>
          {available.map((country) => (
            <option key={country.countryId} value={country.countryId}>
              {country.name}
            </option>
          ))}
        </FilterSelect>
      </ListToolbar>

      {list.error && <ErrorState title={list.error.title} detail={list.error.detail} onRetry={list.reload} />}

      <Busy busy={list.loading}>
        {list.data && (
          <Card>
            {list.data.items.length === 0 ? (
              <EmptyState
                title="No teams yet"
                hint="Until somebody reports into a manager, every manager sees only their own work."
                action={manage && <Button onClick={() => setCreating(true)}>New team</Button>}
              />
            ) : (
              <Table>
                <thead>
                  <tr>
                    <SortableTh field="name" sort={sort} onSortChange={(next) => refine(() => setSort(next || DEFAULT_SORT))}>
                      Team
                    </SortableTh>
                    <SortableTh field="manager" sort={sort} onSortChange={(next) => refine(() => setSort(next || DEFAULT_SORT))}>
                      Manager
                    </SortableTh>
                    <SortableTh field="members" sort={sort} onSortChange={(next) => refine(() => setSort(next || DEFAULT_SORT))} numeric>
                      Reports
                    </SortableTh>
                    <Th>Countries covered</Th>
                    <SortableTh field="status" sort={sort} onSortChange={(next) => refine(() => setSort(next || DEFAULT_SORT))}>
                      Status
                    </SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {list.data.items.map((team) => (
                    <Tr key={team.id} onClick={() => navigate(`/teams/${team.id}`)}>
                      <Td>
                        <span className="font-medium">{team.name}</span>
                        {team.description && (
                          <span className="block text-[12px] text-muted">{team.description}</span>
                        )}
                      </Td>
                      <Td>{team.managerDisplayName}</Td>
                      <Td numeric>{team.memberCount}</Td>
                      <Td>
                        {team.countries.length === 0 ? (
                          <Badge tone="warning">None</Badge>
                        ) : (
                          <span className="font-mono text-[12px]">{team.countries.join(' ')}</span>
                        )}
                      </Td>
                      <Td>{team.isActive ? <Badge tone="accent">Active</Badge> : <Badge>Archived</Badge>}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            )}

            <Pagination
              page={list.data.page}
              pageSize={list.data.pageSize}
              totalCount={list.data.totalCount}
              totalPages={list.data.totalPages}
              onPageChange={setPage}
              onPageSizeChange={(next) => refine(() => setPageSize(next))}
            />
          </Card>
        )}
      </Busy>

      <CreateTeam
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(id, name) => {
          setCreating(false)
          toast.show(`${name} created`, 'success')
          navigate(`/teams/${id}`)
        }}
      />
    </>
  )
}

function CreateTeam({
  open,
  onClose,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  onCreated: (id: string, name: string) => void
}) {
  const toast = useToast()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [managerUserId, setManagerUserId] = useState('')
  const [saving, setSaving] = useState(false)

  const users = useAsync<UserListItem[]>(
    (signal) => api.get<Paged<UserListItem>>('/users', { pageSize: 200 }, signal).then((paged) => paged.items),
    [],
  )

  async function create() {
    setSaving(true)
    try {
      const created = await api.post<{ id: string }>('/sales-teams', {
        name,
        description: description || null,
        managerUserId,
      })

      setName('')
      setDescription('')
      setManagerUserId('')
      onCreated(created.id, name)
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.detail ?? cause.title : 'Could not create the team', 'error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      title="New sales team"
      description="Name the team and say who runs it. Reps are added afterwards."
      onClose={onClose}
      loading={users.loading}
      error={users.error}
      onRetry={users.reload}
      footer={
        <>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            loading={saving}
            disabled={!name.trim() || !managerUserId}
            onClick={() => void create()}
          >
            Create team
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Name" required>
          {(id) => (
            <Input
              id={id}
              value={name}
              placeholder="Enterprise"
              onChange={(event) => setName(event.target.value)}
            />
          )}
        </Field>

        <Field label="Manager" required hint="Sees every member's customers, quotations and figures.">
          {(id) => (
            <Select id={id} value={managerUserId} onChange={(event) => setManagerUserId(event.target.value)}>
              <option value="">Choose a manager</option>
              {users.data
                ?.filter((user) => user.isActive)
                .map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.displayName} · {user.roles.join(', ') || 'No role'}
                  </option>
                ))}
            </Select>
          )}
        </Field>

        <Field label="Description" hint="What this team sells, or who it covers.">
          {(id) => (
            <Textarea
              id={id}
              rows={2}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          )}
        </Field>
      </div>
    </Dialog>
  )
}

export function TeamDetailPage() {
  const { id } = useParams()

  return id ? <Team teamId={id} /> : <ErrorState title="No team" detail="That link is missing a team id." />
}

/**
 * One sales team, as a page rather than a dialog.
 *
 * Details are edited as a form and saved with a button; members are added and removed
 * immediately. That split is deliberate - adding a rep is not a draft edit, and pretending
 * it is would make Save mean two different things on one screen.
 */
function Team({ teamId }: { teamId: string }) {
  const { can } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()
  const [form, setForm] = useState<Partial<SalesTeamDetail>>({})
  const [adding, setAdding] = useState('')
  const [saving, setSaving] = useState(false)
  const [busy, setBusy] = useState(false)
  const [disbanding, setDisbanding] = useState(false)
  const [tab, setTab] = useState<'details' | 'members' | 'history'>('details')

  const canManage = can('salesteam.manage')

  const detail = useAsync<SalesTeamDetail>(
    (signal) =>
      api.get<SalesTeamDetail>(`/sales-teams/${teamId}`, undefined, signal).then((loaded) => {
        setForm(loaded)
        return loaded
      }),
    [teamId],
  )

  const assignable = useAsync<AssignableUser[]>(
    (signal) =>
      canManage ? api.get<AssignableUser[]>('/sales-teams/assignable', undefined, signal) : Promise.resolve([]),
    [teamId, canManage],
  )

  // Every active user, not just the unassigned: a manager can already belong to a team,
  // including the one being edited, so the assignable list is the wrong source here.
  const users = useAsync<UserListItem[]>(
    (signal) =>
      canManage
        ? api.get<Paged<UserListItem>>('/users', { pageSize: 200 }, signal).then((paged) => paged.items)
        : Promise.resolve([]),
    [teamId, canManage],
  )

  const apply = useCallback((values: Partial<SalesTeamDetail>) => setForm(values), [])
  const changes = useUnsavedChanges(form, detail.data, apply, EDITED)
  const guard = useLeaveGuard(changes)

  function set<K extends keyof SalesTeamDetail>(key: K, value: SalesTeamDetail[K]) {
    setForm((current) => ({ ...current, [key]: value }))
  }

  async function save() {
    setSaving(true)
    try {
      await api.patch(`/sales-teams/${teamId}`, {
        name: form.name,
        description: form.description ?? null,
        managerUserId: form.managerUserId,
        isActive: form.isActive,
      })

      // Before the reload, so the page is clean the moment the save lands rather than
      // for as long as the round trip takes.
      changes.accept()
      toast.show('Team saved', 'success')
      detail.reload()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.detail ?? cause.title : 'Could not save the team', 'error')
    } finally {
      setSaving(false)
    }
  }

  /** A membership change, which takes effect as it is made. */
  async function run(action: () => Promise<unknown>, done: string) {
    setBusy(true)
    try {
      await action()
      toast.show(done, 'success')
      detail.reload()
      assignable.reload()
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.detail ?? cause.title : 'That did not work', 'error')
    } finally {
      setBusy(false)
    }
  }

  async function disband() {
    setBusy(true)
    try {
      await api.del(`/sales-teams/${teamId}`)
      toast.show('Team disbanded', 'success')

      // Past the guard on the way out: asking whether to keep unsaved edits to a team
      // that has just been disbanded is a question with no useful answer.
      setDisbanding(false)
      guard.bypass(() => navigate('/teams', { replace: true }))
    } catch (cause) {
      toast.show(cause instanceof ApiError ? cause.detail ?? cause.title : 'Could not disband the team', 'error')
      setBusy(false)
    }
  }

  // Only the very first load replaces the screen. A reload after a save keeps what is
  // already on it and puts the spinner over the top - blanking a page somebody just
  // saved is the same jump as a loading block pushing a listing down.
  if (detail.loading && !detail.data) return <Loading />
  if (detail.error) {
    return <ErrorState title={detail.error.title} detail={detail.error.detail} onRetry={detail.reload} />
  }

  const members = detail.data?.members ?? []
  const activeUsers = (users.data ?? []).filter((user) => user.isActive)
  const managerListed = activeUsers.some((user) => user.id === detail.data?.managerUserId)

  return (
    <>
      <PageHeader
        eyebrow="Administration"
        title={form.name ?? 'Sales Team'}
        back={{ label: 'Back to teams', onClick: () => navigate('/teams') }}
      />

      <Tabs
        active={tab}
        onSelect={setTab}
        tabs={[
          { id: 'details', label: 'Details' },
          { id: 'members', label: 'Members', count: members.length },
          { id: 'history', label: 'History' },
        ]}
      />

      {tab === 'details' && (
        <Card>
          <CardHeader
            title="Team"
            hint="Who runs this team, and what it is called. Changes take effect when you save them."
          />

          <FormGrid>
            <Field label="Name" required changed={changes.changed('name')}>
              {(id) => (
                <Input
                  id={id}
                  disabled={!canManage}
                  value={form.name ?? ''}
                  onChange={(event) => set('name', event.target.value)}
                />
              )}
            </Field>

            <Field
              label="Manager"
              required
              changed={changes.changed('managerUserId')}
              hint="Changing the manager moves every report's work into their view, and out of the previous manager's."
            >
              {(id) => (
                <Select
                  id={id}
                  disabled={!canManage}
                  value={form.managerUserId ?? ''}
                  onChange={(event) => set('managerUserId', event.target.value)}
                >
                  {/* The sitting manager is always an option, even if they have since
                      been deactivated. Leaving them out would show the dropdown resting
                      on somebody else and turn a glance at the page into a change. */}
                  {!managerListed && detail.data && (
                    <option value={detail.data.managerUserId}>
                      {detail.data.managerDisplayName}
                    </option>
                  )}
                  {activeUsers.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.displayName} · {user.roles.join(', ') || 'No role'}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Field
              label="Description"
              changed={changes.changed('description')}
              hint="What this team sells, or who it covers."
            >
              {(id) => (
                <Textarea
                  id={id}
                  rows={2}
                  disabled={!canManage}
                  value={form.description ?? ''}
                  onChange={(event) => set('description', event.target.value)}
                />
              )}
            </Field>

            <Field
              label="Status"
              changed={changes.changed('isActive')}
              hint="Archiving keeps the team, its members and its history - but the reporting line only counts while the team is active, so its manager stops seeing their reports' work."
            >
              {(id) => (
                <Select
                  id={id}
                  disabled={!canManage}
                  value={form.isActive === false ? 'archived' : 'active'}
                  onChange={(event) => set('isActive', event.target.value === 'active')}
                >
                  <option value="active">Active</option>
                  <option value="archived">Archived</option>
                </Select>
              )}
            </Field>
          </FormGrid>

          {canManage && (
            <SaveActions
              count={changes.fields.length}
              saving={saving}
              saveLabel="Save team"
              canSave={Boolean(form.name?.trim() && form.managerUserId)}
              onSave={() => void save()}
              onDiscard={changes.discard}
            >
              <Button size="sm" variant="danger" disabled={busy} onClick={() => setDisbanding(true)}>
                Disband team
              </Button>
            </SaveActions>
          )}
        </Card>
      )}

      {tab === 'members' && (
        <Card>
          <CardHeader
            title={`Reports · ${members.length}`}
            hint="Adding and removing a rep takes effect immediately - it is not part of the details above."
          />

          {members.length === 0 ? (
            <EmptyState
              title="Nobody reports into this team yet"
              hint="Until somebody does, its manager sees only their own work."
            />
          ) : (
            <ul className="flex flex-col border-t border-line-soft">
              {members.map((member) => (
                <li
                  key={member.userId}
                  className="flex items-center justify-between gap-3 border-t border-line-soft px-5 py-3 first:border-t-0"
                >
                  <div className="min-w-0">
                    <p className="truncate text-[13px] font-medium">{member.displayName}</p>
                    <p className="truncate text-[12px] text-muted">
                      {member.roles.join(', ') || 'No role'}
                      {member.countries.length > 0 && (
                        <span className="font-mono"> · {member.countries.join(' ')}</span>
                      )}
                    </p>
                  </div>

                  {canManage && (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy}
                      onClick={() =>
                        void run(
                          () => api.del(`/sales-teams/${teamId}/members/${member.userId}`),
                          `${member.displayName} removed`,
                        )
                      }
                    >
                      Remove
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}

          {canManage && (
            <div className="flex flex-col gap-1.5 border-t border-line-soft px-5 py-4">
              {/* The hint sits under the row rather than inside the field. A field is
                  three stacked parts - label, control, hint - so aligning the button to
                  the field's end put it level with the sentence, a line below the select
                  it belongs to. */}
              <div className="flex items-end gap-2">
                <div className="w-full max-w-xs">
                  <Field label="Add a rep">
                    {(id) => (
                      <Select id={id} value={adding} onChange={(event) => setAdding(event.target.value)}>
                        <option value="">Choose someone</option>
                        {assignable.data
                          ?.filter((user) => user.id !== detail.data?.managerUserId)
                          .map((user) => (
                            <option key={user.id} value={user.id}>
                              {user.displayName} · {user.roles.join(', ') || 'No role'}
                            </option>
                          ))}
                      </Select>
                    )}
                  </Field>
                </div>
                <Button
                  disabled={!adding || busy}
                  onClick={() =>
                    void run(async () => {
                      await api.post(`/sales-teams/${teamId}/members`, { userId: adding })
                      setAdding('')
                    }, 'Added to the team')
                  }
                >
                  Add
                </Button>
              </div>
              <p className="text-[12px] text-muted">Only people who do not already report somewhere.</p>
            </div>
          )}
        </Card>
      )}

      {tab === 'history' && <AuditLogPanel entityType="SalesTeam" entityId={teamId} className="" />}

      <Dialog
        open={disbanding}
        title="Disband this team?"
        onClose={() => setDisbanding(false)}
        footer={
          <>
            <Button size="sm" onClick={() => setDisbanding(false)}>
              Keep it
            </Button>
            <Button size="sm" variant="danger" loading={busy} onClick={() => void disband()}>
              Disband
            </Button>
          </>
        }
      >
        <p className="text-[13px] text-ink-2">
          {members.length === 0
            ? 'Nobody reports into this team, so nothing moves.'
            : `Its ${members.length} report${members.length === 1 ? '' : 's'} keep their work and become assignable to another team.`}{' '}
          The team's history is kept.
        </p>
      </Dialog>

      <LeaveGuard guard={guard} />
    </>
  )
}
